import type { EventTimeline } from '../../components/core/Timelines';

/**
 * Order-list status filter tabs (Shortlist, Confirmed, Inquiries, …).
 * Extracted from planning.tsx so persisted-state modules can import it without
 * creating an import cycle with the (very large) planning page module.
 */
export enum StatusFilter {
  'MAPPING_REQUIRED',
  'CANCELLED' = 'CANCELLED',
  'INQUIRY' = 'INQUIRY',
  'OPEN' = 'OPEN',
  'COA_PRELIMS' = 'COA_PRELIMS',
  'PUBLISHED' = 'PUBLISHED',
  'COMPLETED' = 'COMPLETED',
  'SCENARIO' = 'SCENARIO',
  'TIMELINE_SELECTION' = 'TIMELINE_SELECTION',
  'UNPUBLISHED' = 'UNPUBLISHED',
  'TEMPLATE' = 'TEMPLATE',
  'REGION' = 'REGION',
  'SHORTLIST' = 'SHORTLIST',
  'ORPHAN_PARCELS' = 'ORPHAN PARCELS',
  'CARGO_BOOK' = 'CARGO_BOOK',
}

/** Schedule / vessel-group category filter (All, Fleet, Relet, Modified, Updated, …). */
export type CategoryFilter =
  | 'all'
  | 'shortlist'
  | 'estimates'
  | 'updated'
  | 'open'
  | 'reserved'
  | 'modified'
  | EventTimeline['type'];

/**
 * Category to display/filter by. While 'shortlist' is selected but no
 * shortlisted vessel currently has a timeline — because the shortlist was
 * cleared, or the scenario's data is still loading / momentarily swapped for the
 * empty dummy scenario during a background reload — show 'all' so the timeline
 * is never left blank.
 *
 * This is pure and derived: it must never be written back to the persisted
 * category atom, otherwise a transient empty (load, scenario swap, cross-tab
 * sync) would permanently clobber a non-empty 'shortlist' selection. Persisting
 * the fallback is done separately and edge-triggered on the scenario's own
 * shortlist count (see PlanningImpl).
 */
export function resolveDisplayCategory(
  selectedCategory: CategoryFilter,
  shortlistTimelineCount: number,
): CategoryFilter {
  return selectedCategory === 'shortlist' && shortlistTimelineCount === 0
    ? 'all'
    : selectedCategory;
}

/**
 * Whether to persist the 'all' fallback for the Shortlist category. True only
 * when the scenario's shortlist genuinely transitions non-empty -> empty (the
 * user cleared it) while the Shortlist filter is active.
 *
 * Counts are the scenario's own `vesselsShortlist` length, where `null` means
 * "not loaded" (the empty dummy scenario carries no `vesselsShortlist`). A
 * previous `null` (load / scenario swap) never satisfies `prev > 0`, so
 * transients don't persist a spurious reset; only a real number-to-zero drop
 * does.
 */
export function shouldPersistShortlistFallback(
  prevShortlistCount: number | null,
  shortlistCount: number | null,
  selectedCategory: CategoryFilter,
): boolean {
  return (
    selectedCategory === 'shortlist' &&
    prevShortlistCount !== null &&
    prevShortlistCount > 0 &&
    shortlistCount === 0
  );
}
